import { DOCUMENT } from '@angular/common';
import {
  ApplicationRef,
  EnvironmentInjector,
  Inject,
  Injectable,
  Injector,
  Type,
  ViewRef,
  createComponent,
} from '@angular/core';
import { ModalTooltipOptions } from '@shared/v2/models';
import { ModalTooltip } from './modal-tooltip';
import { SD_MODAL_TOOLTIP_OPTIONS_TOKEN } from './modal-tooltip-options.token';
import { ModalTooltipComponent } from './modal-tooltip.component';

@Injectable({
  providedIn: 'root',
})
export class ModalTooltipService {
  private stack: ModalTooltip[] = [];
  private defaultOptions: ModalTooltipOptions = {
    lifeDuration: 3000,
  };

  constructor(
    private readonly appRef: ApplicationRef,
    private readonly injector: EnvironmentInjector,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  open(content: Type<unknown>, options: ModalTooltipOptions = this.defaultOptions): ModalTooltip {
    const sdModalTooltip = new ModalTooltip();
    const mergedOptions: ModalTooltipOptions = { ...this.defaultOptions, ...options };

    const elementInjector = Injector.create({
      parent: this.injector,
      providers: [
        { provide: ModalTooltip, useValue: sdModalTooltip },
        { provide: SD_MODAL_TOOLTIP_OPTIONS_TOKEN, useValue: mergedOptions },
      ],
    });

    const contentRef = createComponent(content, {
      environmentInjector: this.injector,
      elementInjector,
    });

    const modalRef = createComponent(ModalTooltipComponent, {
      environmentInjector: this.injector,
      elementInjector,
      projectableNodes: [[contentRef.location.nativeElement]],
    });

    sdModalTooltip.setContentRef(contentRef);
    sdModalTooltip.setModalRef(modalRef);
    sdModalTooltip.setOnCloseMethod(() => this.destroyTooltipInstance(sdModalTooltip));

    this.attachTooltip(modalRef.location.nativeElement, modalRef.hostView, contentRef.hostView);
    this.stack.push(sdModalTooltip);

    return sdModalTooltip;
  }

  closeActiveModal(): void {
    const sdModalTooltip = this.stack.at(-1);

    if (!sdModalTooltip) {
      return;
    }

    sdModalTooltip.close();
  }

  getActiveModal(): ModalTooltip | undefined {
    return this.stack.at(-1) ?? undefined;
  }

  hasOpenModals(): boolean {
    return this.stack.length > 0;
  }

  private attachTooltip(modalElement: HTMLElement, modalHostView: ViewRef, contentHostView: ViewRef): void {
    this.document.body.appendChild(modalElement);
    this.appRef.attachView(modalHostView);
    this.appRef.attachView(contentHostView);
  }

  private destroyTooltipInstance(sdModalTooltip: ModalTooltip): void {
    const index = this.stack.indexOf(sdModalTooltip);

    if (index === -1) {
      return;
    }

    const instance = this.stack[index];

    this.appRef.detachView(instance.contentRef.hostView);
    this.appRef.detachView(instance.modalRef.hostView);
    instance.contentRef.destroy();
    instance.modalRef.destroy();
    this.stack.splice(index, 1);
  }
}
